/**
 * Merge Duplicates Modal - Allows users to review and resolve duplicate contacts
 */

import React, { useState } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonIcon,
  IonCard,
  IonCardContent,
} from '@ionic/react';
import {
  closeOutline,
  chevronDownOutline,
  chevronUpOutline,
} from 'ionicons/icons';
import { Contact, ContactLogic } from '@/modules/contact';
import { PhoneLogic } from '@/modules/phone';
import type { DuplicateContactGroup } from '@/modules/contact/reducer';
import { pxToRem } from '@/modules/config/constants';

interface MergeDuplicatesModalProps {
  isOpen: boolean;
  duplicatesToResolve: DuplicateContactGroup[];
  onResolve: (index: number, resolution: 'merge' | 'dismiss') => void;
  onMergeAll: () => void;
}

interface CollapsedContactItemProps {
  contact: Contact;
  isLast: boolean;
}

const CollapsedContactItem: React.FC<CollapsedContactItemProps> = ({
  contact,
  isLast,
}) => {
  const primaryPhone = ContactLogic.getPrimaryPhone(contact);
  const formattedPhone = primaryPhone
    ? ContactLogic.getFormattedPhone(contact)
    : null;
  const categories =
    contact.listingCategories.length > 0
      ? contact.listingCategories.join(', ')
      : null;

  return (
    <div
      style={{
        marginBottom: isLast ? '8px' : '12px',
        paddingLeft: '4px',
      }}
    >
      <div style={{ fontSize: pxToRem(16), fontWeight: 500 }}>
        {ContactLogic.displayName(contact)}
      </div>
      <div
        style={{
          fontSize: pxToRem(14),
          color: '#666',
          marginTop: '4px',
        }}
      >
        {formattedPhone || categories || 'No contact info'}
      </div>
    </div>
  );
};

interface ExpandedContactItemProps {
  contact: Contact;
  isLast: boolean;
}

const ExpandedContactItem: React.FC<ExpandedContactItemProps> = ({
  contact,
  isLast,
}) => {
  return (
    <div
      style={{
        marginBottom: isLast ? '12px' : '16px',
        padding: '12px',
        backgroundColor: '#f5f5f5',
        borderRadius: '8px',
      }}
    >
      <div
        style={{
          fontSize: pxToRem(16),
          fontWeight: 500,
          marginBottom: '8px',
        }}
      >
        {ContactLogic.displayName(contact)}
      </div>
      {contact.tels.length > 0 && (
        <div style={{ marginBottom: '8px' }}>
          {contact.tels.map(tel => (
            <div
              key={tel.localId}
              style={{
                fontSize: pxToRem(14),
                color: '#666',
                marginBottom: '4px',
              }}
            >
              {ContactLogic.getFormattedPhone({
                ...contact,
                tels: [tel],
              })}{' '}
              ({PhoneLogic.getPhoneTypeLabel(tel.type)}
              {tel.pref !== undefined ? `, pref: ${tel.pref}` : ''})
            </div>
          ))}
        </div>
      )}
      {contact.listingCategories.length > 0 && (
        <div style={{ fontSize: pxToRem(14), color: '#666' }}>
          Lists: {contact.listingCategories.join(', ')}
        </div>
      )}
    </div>
  );
};

const MergeDuplicatesModal: React.FC<MergeDuplicatesModalProps> = ({
  isOpen,
  duplicatesToResolve,
  onResolve,
  onMergeAll,
}) => {
  // Track expanded state for each group independently
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());

  const toggleGroup = (index: number) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  if (duplicatesToResolve.length === 0) return null;

  return (
    <IonModal
      isOpen={isOpen}
      // The duplicates stay out of the address book until each group is
      // merged or dismissed, so the dialog closes only by answering it. Ionic
      // dismisses for the backdrop, Escape and Android's back button with the
      // 'backdrop' role (a swipe with 'gesture'); refuse those, and allow the
      // role-less dismiss that follows isOpen going false once all are answered.
      canDismiss={async (_data, role) =>
        role !== 'backdrop' && role !== 'gesture'
      }
      backdropDismiss={false}
      data-testid="merge-duplicates-modal"
    >
      <IonHeader>
        <IonToolbar>
          <IonTitle>Merge duplicates</IonTitle>
          <IonButtons slot="end">
            <IonButton disabled>
              <IonIcon slot="icon-only" icon={closeOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div style={{ padding: '20px' }}>
          {duplicatesToResolve.map((item, groupIndex) => {
            const isExpanded = expandedGroups.has(groupIndex);
            const sortedContacts = ContactLogic.sortBySource(item);

            // For collapsed view: show first two items from sorted array
            const contactsToShow = sortedContacts.slice(0, 2);
            const remainingCount =
              sortedContacts.length > 2 ? sortedContacts.length - 2 : 0;

            return (
              <IonCard key={groupIndex} style={{ marginBottom: '16px' }}>
                <IonCardContent>
                  {!isExpanded ? (
                    // Collapsed view: show first two contacts
                    <>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '12px',
                        }}
                      >
                        <IonButton
                          fill="clear"
                          onClick={() => toggleGroup(groupIndex)}
                          style={{
                            '--padding-start': '4px',
                            '--padding-end': '4px',
                          }}
                        >
                          <IonIcon
                            slot="icon-only"
                            icon={chevronDownOutline}
                            style={{ fontSize: pxToRem(20) }}
                          />
                        </IonButton>
                      </div>
                      {contactsToShow.map((duplicate, index) => (
                        <CollapsedContactItem
                          key={duplicate.contact.localId}
                          contact={duplicate.contact}
                          isLast={index === contactsToShow.length - 1}
                        />
                      ))}
                      {remainingCount > 0 && (
                        <div
                          style={{
                            fontSize: pxToRem(14),
                            color: '#666',
                            marginTop: '8px',
                            fontStyle: 'italic',
                          }}
                        >
                          +{remainingCount} more
                        </div>
                      )}
                    </>
                  ) : (
                    // Expanded view: show all contacts
                    <>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '16px',
                        }}
                      >
                        <IonButton
                          fill="clear"
                          onClick={() => toggleGroup(groupIndex)}
                          style={{
                            '--padding-start': '4px',
                            '--padding-end': '4px',
                          }}
                        >
                          <IonIcon
                            slot="icon-only"
                            icon={chevronUpOutline}
                            style={{ fontSize: pxToRem(20) }}
                          />
                        </IonButton>
                      </div>
                      {sortedContacts.map((duplicate, index) => (
                        <ExpandedContactItem
                          key={duplicate.contact.localId}
                          contact={duplicate.contact}
                          isLast={index === sortedContacts.length - 1}
                        />
                      ))}
                    </>
                  )}

                  <div
                    style={{
                      display: 'flex',
                      gap: '12px',
                      marginTop: '20px',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <IonButton
                      fill="outline"
                      color="medium"
                      onClick={() => onResolve(groupIndex, 'dismiss')}
                    >
                      Dismiss
                    </IonButton>
                    <IonButton
                      color="primary"
                      onClick={() => onResolve(groupIndex, 'merge')}
                    >
                      Merge
                    </IonButton>
                  </div>
                </IonCardContent>
              </IonCard>
            );
          })}

          {duplicatesToResolve.length > 1 && (
            <div
              style={{
                position: 'fixed',
                bottom: '20px',
                right: '20px',
                zIndex: 1000,
              }}
            >
              <IonButton color="primary" onClick={onMergeAll}>
                Merge all
              </IonButton>
            </div>
          )}
        </div>
      </IonContent>
    </IonModal>
  );
};

export default MergeDuplicatesModal;
